'use client'
import Section from 'components/Section'
import { _get } from 'utils/shared'
import { Faqs } from 'pageSections/Faqs'
import { Grid } from '@achieve/ascend'
import { Breadcrumbs } from 'components/Breadcrumbs'
import { AchieveLink } from 'components/AchieveLink'
import { BreadcrumbHomeLink } from 'components/BreadcrumbHomeLink'
import { GLOSSARY_PAGE } from 'constants/navigation'
import {
  Typography as TypographyNoMui,
  TypographyLongForm as TypographyLongFormNoMui,
} from 'components/Typography'
import { BLOCKS } from '@contentful/rich-text-types'
import { Pill } from 'components/Pill'
import { getSlug } from 'utils/conversions'
import { ArticleListCarousel } from 'components/ArticleListCarousel'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CardAppCTA } from 'components/Cards'
import { LayoutContext } from '@/providers/LayoutProvider'
import { FdrPage } from '@/utils/environmentConfig'
import { ArticleTOC } from '../ArticleTOC'
import { removeSpecialCharacters } from '@/utils/shared'
import { GlossaryPagination } from 'components/GlossaryPagination'

// Helper function to extract H2 headings from Contentful rich text content (server-side safe)
function extractHeadingsFromContent(content) {
  const headings = []
  if (content?.content && Array.isArray(content.content)) {
    for (let i = 0; i < content.content.length; i++) {
      const element = content.content[i]
      if (element.nodeType === BLOCKS.HEADING_2) {
        const sectionContent = _get(element, 'content[0].value') ?? ''
        headings.push({
          text: sectionContent,
          id: removeSpecialCharacters(
            `${BLOCKS.HEADING_2}-${sectionContent.slice(0, 50).replaceAll(' ', '-')}`
          ),
        })
      }
    }
  }
  return headings
}

function TermView({ content, pageHost, pagination }) {
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const refs = useRef({})

  // Extract data from content first (before useEffect hooks)
  const title = _get(content, 'items[0].fields.title')
  const pageSummary = _get(content, 'items[0].fields.pageSummary')
  const mainBody = _get(content, 'items[0].fields.mainBody')
  const ctaCard = _get(content, 'items[0].fields.ctaCard')
  const additionalInfo = _get(content, 'items[0].fields.additionalInfo')
  const faq = _get(content, 'items[0].fields.faq')
  const externalFaq = _get(content, 'items[0].fields.externalFaq')
  const associatedTerm = _get(content, 'items[0].fields.associatedTerm')
  const relatedArticles = _get(content, 'items[0].fields.relatedArticles')
  const externalRelatedArticles = _get(content, 'items[0].fields.externalRelatedArticles')

  // Generate table of contents server-side (useMemo ensures it only runs once)
  const tableOfContents = useMemo(() => {
    const articles = []

    // Extract H2s from mainBody
    articles.push(...extractHeadingsFromContent(mainBody))

    // Extract H2s from additionalInfo
    articles.push(...extractHeadingsFromContent(additionalInfo))

    return articles
  }, [mainBody, additionalInfo])

  // Track active section for TOC highlighting (client-side only)
  const [activeSection, setActiveSection] = useState(tableOfContents?.[0]?.id)

  // Set up IntersectionObserver for TOC active section tracking (client-side only)
  useEffect(() => {
    if (!tableOfContents || tableOfContents.length === 0) return

    // Set initial active section
    setActiveSection(tableOfContents[0]?.id)

    // Get DOM references for all heading elements
    const currentRefs = {}
    tableOfContents.forEach(({ id }) => {
      const element = document.getElementById(id)
      if (element) {
        currentRefs[id] = element
        refs.current[id] = element
      }
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id)
        })
      },
      {
        root: null,
        rootMargin: '-30% 0px -30% 0px', // Adjust rootMargin to observe the middle of the page
        threshold: 0, // Threshold can be 0 to detect any intersection
      }
    )

    // Observe all heading elements
    Object.values(currentRefs).forEach((element) => {
      observer.observe(element)
    })

    // Cleanup observer on unmount
    return () => {
      Object.values(currentRefs).forEach((element) => {
        observer.unobserve(element)
      })
    }
  }, [tableOfContents])

  const ArticleCarousel = useMemo(
    () => (
      <ArticleListCarousel
        relatedArticles={relatedArticles}
        pageHost={pageHost}
        unifiedArticleLink
      />
    ),
    [relatedArticles, pageHost]
  )

  const ExternalArticleCarousel = useMemo(
    () => (
      <ArticleListCarousel
        relatedArticles={externalRelatedArticles}
        isExternal
        pageHost={pageHost}
        unifiedArticleLink
      />
    ),
    [externalRelatedArticles, pageHost]
  )

  return (
    <>
      <Section className={'overflow-visible mb-8'}>
        <div
          className={'term-view-grid grid gap-x-4 ' + 'grid-cols-1 ' + 'lg:grid-cols-[7fr_3fr_0fr]'}
          style={{
            gridTemplateRows: 'auto auto',
            gridTemplateAreas: `"title" "sidebar" "body"`,
          }}
        >
          {/* Override grid-template-areas for desktop via a style tag scoped by a unique class */}
          <style>{`
            @media (min-width: 1024px) {
              .term-view-grid {
                grid-template-areas:
                  "title sidebar spacer-r"
                  "body sidebar spacer-r" !important;
                grid-template-rows: auto 1fr !important;
                align-items: start;
              }
            }
          `}</style>

          {/* Title area */}
          <div className={'flex flex-col gap-3'} style={{ gridArea: 'title' }}>
            <div className={'mt-3'}>
              <Breadcrumbs
                aria-label="breadcrumb"
                className={
                  'pb-0 [&_a]:text-xs [&_div]:text-xs [&_li:hover]:text-blue-500 [&_li:last-child]:text-blue-500'
                }
              >
                <BreadcrumbHomeLink
                  track={{
                    list_name: 'Breadcrumb',
                    click_text: `Home Page`,
                    click_id: `Home Page`,
                    click_position: '0',
                    nav_link_section: 'Breadcrumb',
                    track_event: 'page_nav_click',
                    event_action: 'link_click',
                    event_type: 'link_click',
                  }}
                />
                <AchieveLink
                  href={`${GLOSSARY_PAGE}`}
                  track={{
                    list_name: 'Breadcrumb',
                    click_text: 'Financial Term Glossary',
                    click_id: 'Financial Term Glossary',
                    click_position: '0',
                    nav_link_section: 'Breadcrumb',
                    track_event: 'page_nav_click',
                    event_action: 'link_click',
                    event_type: 'link_click',
                  }}
                  underline="hover"
                  color="inherit"
                >
                  Financial Term Glossary
                </AchieveLink>
                <div>{title}</div>
              </Breadcrumbs>
            </div>
            <div
              className={
                'border-b-neutral-150 pb-8 border-b border-solid [&_h1]:text-2xl lg:[&_h1]:text-4xl border-t-0 border-x-0 mr-8'
              }
            >
              <TypographyNoMui
                variant="displayLg"
                content={title}
                component="h1"
                className={'my-0'}
              />
            </div>
          </div>

          {/* Sidebar: Page Summary and Table of Contents */}
          {(pageSummary || tableOfContents?.length >= 1) && (
            <div
              className={'lg:sticky lg:top-24 lg:self-start lg:pl-4 lg:pt-10'}
              style={{
                gridArea: 'sidebar',
                maxHeight: undefined,
              }}
            >
              <div className={'lg:overflow-y-auto lg:max-h-[calc(100vh-6rem)]'}>
                {pageSummary && (
                  <div className={'mb-8'}>
                    <TypographyLongFormNoMui
                      content={pageSummary}
                      variantOverride={{
                        [BLOCKS.HEADING_2]: 'headingMd',
                        [BLOCKS.PARAGRAPH]: 'bodyMd',
                      }}
                    />
                  </div>
                )}
                {tableOfContents?.length >= 1 && (
                  <div className={'mb-8 hidden lg:block'}>
                    <ArticleTOC
                      content={tableOfContents}
                      withTitle={false}
                      variantItem="labelSm"
                      activeSection={activeSection}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Body content */}
          <div className={'*:mt-0 [&_*:first-child]:mt-4 mr-8'} style={{ gridArea: 'body' }}>
            <TypographyLongFormNoMui
              content={mainBody}
              variantOverride={{
                [BLOCKS.HEADING_2]: 'headingMd',
                [BLOCKS.PARAGRAPH]: 'bodyMd',
              }}
            />
            <TypographyLongFormNoMui
              content={additionalInfo}
              variantOverride={{
                [BLOCKS.HEADING_2]: 'headingMd',
                [BLOCKS.PARAGRAPH]: 'bodyMd',
              }}
            />
            {ctaCard && <CardAppCTA content={ctaCard.fields} />}
            {pagination && <GlossaryPagination prev={pagination.prev} next={pagination.next} />}
          </div>
        </div>
      </Section>
      {siteEnv !== FdrPage && faq && (
        <Faqs
          content={{
            faqs: { faqSetsCollection: { items: faq?.fields?.faqSets || [] } },
            config: { jsonContent: { color: 'highlight-blue' } },
            show: 3,
            title: {
              fieldName: 'title',
              textContent: faq?.fields?.name || 'Frequently Asked Questions',
            },
          }}
          answerTextVariant="bodygMd"
          questionTextVariant="headingMd"
          titleProps={{
            variants: {
              title: 'headingLg',
              titleFontWeight: 'medium',
            },
          }}
          fullWidth={true}
        />
      )}
      {siteEnv === FdrPage && externalFaq && (
        <Faqs
          content={{
            faqs: { faqSetsCollection: { items: externalFaq?.fields?.faqSets || [] } },
            config: { jsonContent: { color: 'highlight-blue' } },
            show: 3,
            title: {
              fieldName: 'title',
              textContent: externalFaq?.fields?.name || 'Frequently Asked Questions',
            },
          }}
          answerTextVariant="bodygMd"
          questionTextVariant="headingMd"
          titleProps={{
            variants: {
              title: 'headingLg',
              titleFontWeight: 'medium',
            },
          }}
          fullWidth={true}
        />
      )}
      {relatedArticles?.length > 0 && siteEnv !== FdrPage && (
        <Section>
          <Grid container direction="row" justifyContent="center" alignItems="center">
            {ArticleCarousel}
          </Grid>
        </Section>
      )}
      {externalRelatedArticles?.length > 0 && siteEnv === FdrPage && (
        <Section>
          <Grid container direction="row" justifyContent="center" alignItems="center">
            {ExternalArticleCarousel}
          </Grid>
        </Section>
      )}
      {associatedTerm?.length > 0 && (
        <Section className={'mt-8'}>
          <Grid size={{ xs: 12 }}>
            <TypographyNoMui
              variant="headingSm"
              content={`${title} related financial terms`}
              component="h2"
            />
          </Grid>
          <Grid size={{ xs: 12 }} mt={2}>
            <Grid container>
              {associatedTerm?.map((category, idx) => {
                return (
                  <Pill
                    key={idx}
                    className={'text-content-primary text-[25px]'}
                    category={category?.fields?.title}
                    id={idx}
                    path={`${GLOSSARY_PAGE}${getSlug(category?.fields?.letter)}/${
                      category?.fields?.slug
                    }`}
                  />
                )
              })}
            </Grid>
          </Grid>
        </Section>
      )}
    </>
  )
}

export { TermView }
